/**
 * @author Martin Leonov <martin.leonov@ki-wi.cz>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

/* eslint-env browser */

import EventEmitter from 'eventemitter3';

/**
 * @private
 * @typedef {Record<string, *>} AppManager TODO #555
 * @typedef {import('../event-bus').EventBus<EventTypes>} EventBus
 */

/**
 * @typedef {Object} EventTypes
 * @property {IdleEventListener} idle
 */

/**
 * Vyvolaná událost o neaktivitě uživatele.
 * @callback IdleEventListener
 * @returns {void}
 */

/**
 * Umožnuje zjišťovat neaktivitu.
 */
export default class Idle {
  /**
   * Vytvoří novou instanci.
   * @param {AppManager} appManager
   * @param {boolean} hasIdleTime
   */
  constructor(appManager, hasIdleTime) {
    this.appManager = appManager;
    /** @type {EventEmitter<EventTypes>} */
    this.emitter = new EventEmitter();

    /** @type {ReturnType<typeof setTimeout> | undefined} */
    this.idleTimeout = undefined;
    this.hasIdleTime = hasIdleTime;
  }

  /**
   * Zkontroluje zda již uplynula požadovaná doba neaktivity.
   * @private
   * @param {number} ms
   * @returns {Promise<void>}
   */
  async checkIdle(ms) {
    // Pokud spadne volání getIdle, tak se zapne pevný časovač, který nebude zohledňovat neaktivitu uživatele, protože ji nelze zjistit.
    const idleTime = await this.appManager.getIdleTime();
    if (idleTime >= ms) {
      return this.dispatchIdle();
    }

    this.idleTimeout = setTimeout(() => this.checkIdle(ms), 1000);
  }

  /**
   * Vyvolá událost neaktivitě uživatele.
   * @private
   */
  dispatchIdle() {
    this.emitter.emit('idle');
  }

  /** @type {EventBus['off']} */
  off(...args) {
    this.emitter.off(...args);
  }

  /** @type {EventBus['on']} */
  on(...args) {
    this.emitter.on(...args);
  }

  /**
   * Spustí sledování idle stavu.
   * @param {number} ms
   */
  start(ms) {
    window.clearTimeout(this.idleTimeout);
    if (this.hasIdleTime) {
      this.idleTimeout = setTimeout(() => this.checkIdle(ms), 1000);
      return;
    }
    // Platforma neumi getIdleTime(), proto časovač vyvolám po zadaném čase.
    this.idleTimeout = setTimeout(() => this.dispatchIdle(), ms);
  }

  /**
   * Zastaví sledování idle stavu.
   */
  stop() {
    clearTimeout(this.idleTimeout);
  }
}
